import React, { forwardRef } from 'react';

// material-ui
import Card, { CardProps } from '@mui/material/Card';
import CardContent, { CardContentProps } from '@mui/material/CardContent';
import CardHeader, { CardHeaderProps } from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';

// project imports
import { ThemeMode } from 'config';
import useConfig from 'hooks/useConfig';

// constant
const headerStyle = {
  '& .MuiCardHeader-action': { mr: 0, my: 0, alignSelf: 'unset' },
};

// ==============================|| CUSTOM MAIN CARD ||============================== //

export interface MainCardProps {
  border?: boolean;
  boxShadow?: boolean;
  children: React.ReactNode | string;
  style?: React.CSSProperties;
  content?: boolean;
  className?: string;
  contentClass?: string;
  contentSX?: CardContentProps['sx'];
  headerSX?: CardHeaderProps['sx'];
  darkTitle?: boolean;
  sx?: CardProps['sx'];
  scrollPage?: boolean;
  fullHeightPage?: boolean;
  insideContent?: boolean;
  secondary?: CardHeaderProps['action'];
  shadow?: string;
  elevation?: number;
  title?: React.ReactNode | string;
  scrollerRef?: React.RefObject<HTMLDivElement>;
  hideDivider?: boolean;
  [key: string]: any;
}

const MainCard = forwardRef<HTMLDivElement, MainCardProps>(
  (
    {
      border = false,
      boxShadow,
      children,
      content = true,
      contentClass = '',
      contentSX = {},
      headerSX = {},
      darkTitle,
      secondary,
      shadow,
      sx = {},
      scrollPage = false,
      fullHeightPage = false,
      insideContent = false,
      title,
      scrollerRef,
      hideDivider = false,
      ...others
    },
    ref,
  ) => {
    const { mode } = useConfig();
    const defaultShadow =
      mode === ThemeMode.DARK ? '0 2px 14px 0 rgb(33 150 243 / 10%)' : '0 2px 14px 0 rgb(32 40 45 / 8%)';

    return (
      <Card
        ref={ref}
        {...others}
        sx={{
          border: border ? '1px solid' : 'none',
          borderColor: 'divider',
          ':hover': {
            boxShadow: boxShadow ? shadow || defaultShadow : 'inherit',
          },
          display: 'flex',
          flexDirection: 'column',

          ...(scrollPage && {
            height: '100%',
          }),
          ...(fullHeightPage && {
            flex: insideContent ? undefined : '1 1 auto',
            height: insideContent ? '100%' : undefined,
            overflow: 'hidden',
          }),
          ...sx,
        }}
      >
        {/* card header and action */}
        {!title && secondary && (
          <CardHeader 
            sx={{ 
              ...headerStyle, 
              ...headerSX,
              ...(hideDivider && { pb: 0 })
            }} 
            title="" 
            action={secondary} 
          />
        )}
        {!darkTitle && title && (
          <CardHeader 
            sx={{ 
              ...headerStyle, 
              ...headerSX,
              ...(hideDivider && { pb: 0 })
            }} 
            title={title} 
            action={secondary} 
          />
        )}
        {darkTitle && title && (
          <CardHeader
            sx={{ 
              ...headerStyle, 
              ...headerSX,
              ...(hideDivider && { pb: 0 })
            }}
            title={<Typography variant="h3">{title}</Typography>}
            action={secondary}
          />
        )}

        {/* content & header divider */}
        {(title || secondary) && !hideDivider && <Divider />}

        {/* card content */}
        {content && (
          <CardContent
            sx={{
              ...(scrollPage && {
                flex: '1 1 auto',
                overflow: 'auto',
              }),
              ...(fullHeightPage && {
                // height: '100%',
                flex: '1 1 auto',
                overflow: 'hidden',
              }),
              ...(insideContent && {
                px: 0,
              }),
              ...contentSX,
            }}
            className={contentClass}
            ref={scrollerRef}
          >
            {children}
          </CardContent>
        )}
        {!content && children}
      </Card>
    );
  },
);
export default MainCard;
